import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Lock, Eye, EyeOff, Loader2, Check, X } from "lucide-react";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { ICON_SIZES } from "@/lib/iconStandards";
import { UtilityPageLayout } from "@/components/layouts/UtilityPageLayout";
import { PillButton } from "@/components/marketing/v2/PillButton";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "signin";
  const { toast } = useToast();

  const requirements = [
    { label: "At least 8 characters", test: (pwd: string) => pwd.length >= 8 },
    { label: "Contains uppercase letter", test: (pwd: string) => /[A-Z]/.test(pwd) },
    { label: "Contains lowercase letter", test: (pwd: string) => /[a-z]/.test(pwd) },
    { label: "Contains a number", test: (pwd: string) => /\d/.test(pwd) },
    { label: "Contains a symbol", test: (pwd: string) => /[^a-zA-Z0-9]/.test(pwd) },
  ];

  const getRequirementStatus = (requirement: { test: (pwd: string) => boolean }) => {
    if (!password) return null;
    return requirement.test(password);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate(`/auth?mode=${mode}`);
      }
    });
  }, [navigate, mode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      toast({
        title: "Password updated",
        description: "Your password has been successfully reset.",
      });

      navigate(`/auth?mode=${mode}`);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <UtilityPageLayout
      icon={Lock}
      title="Reset your password"
      subtitle="Enter your new password below."
      seoTitle="Reset password | SendFeedback.ai"
      canonicalUrl="https://sendfeedback.ai/reset-password"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-2xl bg-muted/50 p-4 space-y-2">
          <p className="text-sm font-medium text-foreground">Password must contain:</p>
          <ul className="space-y-1.5">
            {requirements.map((req) => {
              const status = getRequirementStatus(req);
              return (
                <li key={`req-${req.label}`} className="flex items-center gap-2 text-sm">
                  {status === true && (
                    <Check className={`${ICON_SIZES.sm} text-success flex-shrink-0`} />
                  )}
                  {status === false && (
                    <X className={`${ICON_SIZES.sm} text-destructive flex-shrink-0`} />
                  )}
                  {status === null && (
                    <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30 flex-shrink-0" />
                  )}
                  <span className={status === true ? "text-foreground" : "text-muted-foreground"}>
                    {req.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">New password</Label>
          <div className="relative">
            <Lock className={`absolute left-3 top-3 ${ICON_SIZES.sm} text-muted-foreground`} />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-9 pr-9"
              disabled={loading}
              required
              minLength={6}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className={ICON_SIZES.sm} /> : <Eye className={ICON_SIZES.sm} />}
            </button>
          </div>
          {password && <PasswordStrengthIndicator password={password} />}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm password</Label>
          <div className="relative">
            <Lock className={`absolute left-3 top-3 ${ICON_SIZES.sm} text-muted-foreground`} />
            <Input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-9"
              disabled={loading}
              required
              minLength={6}
            />
          </div>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <PillButton type="submit" variant="accent" size="lg" className="w-full" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className={`${ICON_SIZES.sm} animate-spin`} />
              Updating password...
            </>
          ) : (
            "Reset password"
          )}
        </PillButton>
      </form>
    </UtilityPageLayout>
  );
};

export default ResetPassword;
